<template>
    <div class="services-by-tags-select" @click="toggleMenu">
        <BaseInputOutlined
            label="Услуги"
            hide-details
            persistent-label
            class="select-input"
            append-icon="mdi-menu-down"
            :value="displayValue"
            readonly
        ></BaseInputOutlined>

        <v-menu
            v-model="menuOpen"
            :activator="`.services-by-tags-select .select-input`"
            :close-on-content-click="false"
            offset-y
            max-width="600px"
            min-width="400px"
        >
            <v-card class="services-by-tags-select__menu">
                <v-card-title class="services-by-tags-select__header">
                    <v-text-field
                        v-model="searchQuery"
                        color="mainNew"
                        label="Поиск..."
                        dense
                        hide-details
                        filled
                        append-icon="mdi-magnify"
                        class="services-by-tags-select__search-input"
                    ></v-text-field>
                </v-card-title>
                
                <v-card-text class="services-by-tags-select__content">
                    <div 
                        v-for="group in filteredGroups" 
                        :key="group.groupId" 
                        class="services-by-tags-select__group"
                        :class="{ 'services-by-tags-select__group--expanded': expandedGroups[group.groupId] }"
                    >
                        <div class="services-by-tags-select__group-header" @click.stop="toggleGroup(group.groupId)">
                            <v-checkbox
                                :input-value="isGroupSelected(group)"
                                :indeterminate="isGroupIndeterminate(group)"
                                @click.stop="toggleGroupSelection(group)"
                                hide-details
                                dense
                                class="services-by-tags-select__checkbox"
                            ></v-checkbox>
                            <v-icon class="services-by-tags-select__expand-icon">
                                {{ expandedGroups[group.groupId] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                            </v-icon>
                            <v-icon class="services-by-tags-select__tag-icon">mdi-tag-outline</v-icon>
                            <span class="services-by-tags-select__group-name">{{ group.groupName }}</span>
                        </div>

                        <div v-if="expandedGroups[group.groupId]" class="services-by-tags-select__subgroups">
                                <div
                                    v-for="subgroup in group.subgroups"
                                    :key="subgroup.subgroupId"
                                    class="services-by-tags-select__subgroup"
                                    :class="{ 'services-by-tags-select__subgroup--expanded': subgroup.subgroupName && expandedSubgroups[`${group.groupId}_${subgroup.subgroupId}`] }"
                                >
                                    <div 
                                        v-if="subgroup.subgroupName" 
                                        class="services-by-tags-select__subgroup-header"
                                        @click.stop="toggleSubgroup(group.groupId, subgroup.subgroupId)"
                                    >
                                        <v-checkbox
                                            :input-value="isSubgroupSelected(subgroup)"
                                            :indeterminate="isSubgroupIndeterminate(subgroup)"
                                            @click.stop="toggleSubgroupSelection(subgroup)"
                                            hide-details
                                            dense
                                            class="services-by-tags-select__checkbox"
                                        ></v-checkbox>
                                        <v-icon class="services-by-tags-select__expand-icon services-by-tags-select__expand-icon--subgroup">
                                            {{ expandedSubgroups[`${group.groupId}_${subgroup.subgroupId}`] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                        </v-icon>
                                        <v-icon class="services-by-tags-select__tag-icon">mdi-tag-outline</v-icon>
                                        <span class="services-by-tags-select__subgroup-name">{{ subgroup.subgroupName }}</span>
                                    </div>

                                    <div 
                                        v-if="!subgroup.subgroupName || expandedSubgroups[`${group.groupId}_${subgroup.subgroupId}`]"
                                        class="services-by-tags-select__group-items"
                                    >
                                        <div
                                            v-for="service in subgroup.services"
                                            :key="service.id"
                                            class="services-by-tags-select__item"
                                            @click.stop="toggleServiceSelection(service)"
                                        >
                                            <v-checkbox
                                                :input-value="selectedServices[service.id]"
                                                hide-details
                                                dense
                                                class="services-by-tags-select__checkbox"
                                            ></v-checkbox>
                                            <span class="services-by-tags-select__item-name">{{ service.name }}</span>
                                        </div>
                                    </div>
                                </div>
                        </div>
                    </div>
                </v-card-text>
            </v-card>
        </v-menu>
    </div>
</template>

<script>
import Vue from 'vue'
import BaseInputOutlined from './BaseInputOutlined.vue'

export default Vue.extend({
    name: 'ServicesByTagsSelect',
    
    components: {
        BaseInputOutlined
    },
    
    props: {
        services: {
            type: Array,
            required: true,
            default: () => []
        },
        value: {
            type: Array,
            default: () => []
        },
        tagsMapping: {
            type: Object,
            default: () => ({ groups: {}, subgroups: {} })
        }
    },

    data() {
        return {
            menuOpen: false,
            searchQuery: '',
            expandedGroups: {},
            expandedSubgroups: {},
            selectedServices: {},
            isUpdatingFromValue: false
        }
    },

    computed: {
        // Группируем услуги по тегам кластера Лореаль с двухуровневой структурой
        groupedServices() {
            // Если нет маппинга, возвращаем пустой массив
            if (!this.tagsMapping || !this.tagsMapping.groups) {
                return []
            }

            // Сначала создаем структуру групп из tagsMapping
            const groups = {}
            
            // Проходим по всем группам из маппинга
            Object.keys(this.tagsMapping.groups).forEach(groupKey => {
                groups[groupKey] = {
                    groupId: groupKey,
                    groupName: this.tagsMapping.groups[groupKey],
                    subgroups: {}
                }
            })

            // Проходим по всем подгруппам из маппинга и добавляем их в соответствующие группы
            if (this.tagsMapping.subgroupToGroup) {
                Object.keys(this.tagsMapping.subgroups).forEach(subgroupKey => {
                    // Получаем родительскую группу из маппинга
                    const parentGroupKey = this.tagsMapping.subgroupToGroup[subgroupKey]
                    
                    // Если родительская группа существует, добавляем подгруппу
                    if (parentGroupKey && groups[parentGroupKey]) {
                        groups[parentGroupKey].subgroups[subgroupKey] = {
                            subgroupId: subgroupKey,
                            subgroupName: this.tagsMapping.subgroups[subgroupKey],
                            services: []
                        }
                    }
                })
            }

            // Теперь распределяем услуги по группам и подгруппам
            const servicesWithoutTags = []
            const servicesWithoutGroup = []

            this.services.forEach(service => {
                // Проверяем наличие тегов
                if (!service.tags || !Array.isArray(service.tags)) {
                    servicesWithoutTags.push(service)
                    return
                }

                // Фильтруем только теги кластера Лореаль (любой статус, кроме delayed)
                const lorealTags = service.tags.filter(tag => 
                    tag.cluster === 'loreal' && tag.status !== 'delayed'
                )

                // Если нет тегов Лореаль, добавляем в группу без тегов
                if (lorealTags.length === 0) {
                    servicesWithoutTags.push(service)
                    return
                }

                // Добавляем услугу в каждую группу тегов
                let addedToAnyGroup = false
                lorealTags.forEach(tag => {
                    // Если нет группы и подгруппы, помещаем в "Без группы"
                    if (!tag.group && !tag.subgroup) {
                        servicesWithoutGroup.push(service)
                        return
                    }

                    const groupKey = tag.group
                    const subgroupKey = tag.subgroup

                    // Проверяем, существует ли такая группа в структуре
                    if (groups[groupKey]) {
                        // Если есть подгруппа и она существует в структуре
                        if (subgroupKey && groups[groupKey].subgroups[subgroupKey]) {
                            addedToAnyGroup = true
                            const subgroup = groups[groupKey].subgroups[subgroupKey]
                            if (!subgroup.services.find(s => s.id === service.id)) {
                                subgroup.services.push(service)
                            }
                        } else if (!subgroupKey) {
                            // Если подгруппы вообще нет в теге, добавляем в дефолтную
                            addedToAnyGroup = true
                            if (!groups[groupKey].subgroups['_default']) {
                                groups[groupKey].subgroups['_default'] = {
                                    subgroupId: '_default',
                                    subgroupName: null,
                                    services: []
                                }
                            }
                            if (!groups[groupKey].subgroups['_default'].services.find(s => s.id === service.id)) {
                                groups[groupKey].subgroups['_default'].services.push(service)
                            }
                        }
                        // Если подгруппа указана, но не найдена в структуре - услуга попадет в "Без группы"
                    }
                })

                // Если услуга не была добавлена ни в одну группу, добавляем в "Без группы"
                if (!addedToAnyGroup) {
                    servicesWithoutGroup.push(service)
                }
            })

            // Добавляем группу "Без группы" если есть услуги
            if (servicesWithoutGroup.length > 0) {
                groups['_no_group'] = {
                    groupId: '_no_group',
                    groupName: 'Без группы',
                    subgroups: {
                        '_default': {
                            subgroupId: '_default',
                            subgroupName: null,
                            services: [...new Set(servicesWithoutGroup)] // убираем дубликаты
                        }
                    }
                }
            }

            // Добавляем группу для услуг без тегов
            if (servicesWithoutTags.length > 0) {
                groups['_without_tags'] = {
                    groupId: '_without_tags',
                    groupName: 'Без тегов',
                    subgroups: {
                        '_default': {
                            subgroupId: '_default',
                            subgroupName: null,
                            services: servicesWithoutTags
                        }
                    }
                }
            }

            // Преобразуем в массив, фильтруем пустые группы и сортируем
            return Object.values(groups)
                .map(group => ({
                    ...group,
                    subgroups: Object.values(group.subgroups)
                        .filter(subgroup => subgroup.services.length > 0) // убираем пустые подгруппы
                        .sort((a, b) => (a.subgroupName || '').localeCompare(b.subgroupName || ''))
                }))
                .filter(group => group.subgroups.length > 0) // убираем пустые группы
                .sort((a, b) => {
                    // Специальные группы в конце
                    if (a.groupId === '_without_tags') return 1
                    if (b.groupId === '_without_tags') return -1
                    if (a.groupId === '_no_group') return 1
                    if (b.groupId === '_no_group') return -1
                    return a.groupName.localeCompare(b.groupName)
                })
        },

        filteredGroups() {
            if (!this.searchQuery) {
                return this.groupedServices
            }

            const query = this.searchQuery.toLowerCase()
            return this.groupedServices
                .map(group => ({
                    ...group,
                    subgroups: group.subgroups
                        .map(subgroup => ({
                            ...subgroup,
                            services: subgroup.services.filter(service =>
                                service.name.toLowerCase().includes(query)
                            )
                        }))
                        .filter(subgroup => 
                            subgroup.services.length > 0 || 
                            (subgroup.subgroupName && subgroup.subgroupName.toLowerCase().includes(query))
                        )
                }))
                .filter(group => 
                    group.subgroups.length > 0 || 
                    group.groupName.toLowerCase().includes(query)
                )
        },

        displayValue() {
            const selectedCount = Object.values(this.selectedServices).filter(Boolean).length
            if (selectedCount === 0) {
                return 'Все'
            }
            return `Выбрано ${selectedCount}`
        }
    },

    watch: {
        value: {
            immediate: true,
            handler(newVal) {
                if (!newVal) return
                
                this.isUpdatingFromValue = true
                const selected = {}
                newVal.forEach(serviceId => {
                    selected[serviceId] = true
                })
                this.selectedServices = selected
                this.$nextTick(() => {
                    this.isUpdatingFromValue = false
                })
            }
        },

        selectedServices: {
            deep: true,
            handler() {
                // Предотвращаем циклические обновления
                if (this.isUpdatingFromValue) return
                
                // Используем nextTick для батчинга обновлений
                this.$nextTick(() => {
                    const selected = Object.keys(this.selectedServices)
                        .filter(id => this.selectedServices[id])
                    this.$emit('input', selected)
                })
            }
        },

        searchQuery(newQuery) {
            if (!newQuery) {
                // Если поиск очищен, не меняем состояние раскрытых групп
                return
            }

            const query = newQuery.toLowerCase()
            
            // Автоматически раскрываем группы и подгруппы с найденными услугами
            this.filteredGroups.forEach(group => {
                // Раскрываем группу, если в ней есть результаты
                if (group.subgroups.length > 0) {
                    this.$set(this.expandedGroups, group.groupId, true)
                    
                    // Раскрываем подгруппы, если в них есть услуги или они соответствуют запросу
                    group.subgroups.forEach(subgroup => {
                        if (subgroup.services.length > 0 || 
                            (subgroup.subgroupName && subgroup.subgroupName.toLowerCase().includes(query))) {
                            const subgroupKey = `${group.groupId}_${subgroup.subgroupId}`
                            this.$set(this.expandedSubgroups, subgroupKey, true)
                        }
                    })
                }
            })
        }
    },

    methods: {
        toggleMenu() {
            this.menuOpen = !this.menuOpen
        },

        closeMenu() {
            this.menuOpen = false
        },

        toggleGroup(groupId) {
            this.$set(this.expandedGroups, groupId, !this.expandedGroups[groupId])
        },

        toggleSubgroup(groupId, subgroupId) {
            const key = `${groupId}_${subgroupId}`
            this.$set(this.expandedSubgroups, key, !this.expandedSubgroups[key])
        },

        getAllServicesInGroup(group) {
            const services = []
            group.subgroups.forEach(subgroup => {
                services.push(...subgroup.services)
            })
            return services
        },

        isGroupSelected(group) {
            const allServices = this.getAllServicesInGroup(group)
            return allServices.length > 0 && allServices.every(service => this.selectedServices[service.id])
        },

        isGroupIndeterminate(group) {
            const allServices = this.getAllServicesInGroup(group)
            const selectedInGroup = allServices.filter(service => this.selectedServices[service.id])
            return selectedInGroup.length > 0 && selectedInGroup.length < allServices.length
        },

        toggleGroupSelection(group) {
            const allServices = this.getAllServicesInGroup(group)
            const allSelected = this.isGroupSelected(group)
            const newValue = !allSelected
            
            // Создаем новый объект с обновленными значениями
            const updated = { ...this.selectedServices }
            allServices.forEach(service => {
                updated[service.id] = newValue
            })
            
            // Обновляем весь объект одним махом
            this.selectedServices = updated
        },

        isSubgroupSelected(subgroup) {
            return subgroup.services.length > 0 && subgroup.services.every(service => this.selectedServices[service.id])
        },

        isSubgroupIndeterminate(subgroup) {
            const selectedInSubgroup = subgroup.services.filter(service => this.selectedServices[service.id])
            return selectedInSubgroup.length > 0 && selectedInSubgroup.length < subgroup.services.length
        },

        toggleSubgroupSelection(subgroup) {
            const allSelected = this.isSubgroupSelected(subgroup)
            const newValue = !allSelected
            
            // Создаем новый объект с обновленными значениями
            const updated = { ...this.selectedServices }
            subgroup.services.forEach(service => {
                updated[service.id] = newValue
            })
            
            // Обновляем весь объект одним махом
            this.selectedServices = updated
        },

        toggleServiceSelection(service) {
            this.$set(this.selectedServices, service.id, !this.selectedServices[service.id])
        }
    }
})
</script>

<style lang="sass" scoped>
.services-by-tags-select
    ::v-deep .select-input
        cursor: pointer
        
        input
            cursor: pointer

    &__menu
        max-width: 600px !important

    &__header
        padding: 0
        border-bottom: 1px solid rgba(0, 0, 0, 0.12)

    &__search-input
        width: 100%
        padding-top: 0

    &__content
        max-height: 400px
        overflow-y: auto
        padding: 0

    &__group
        position: relative
        
        &:last-child
            .services-by-tags-select__group-header
                border-bottom: none

        &--expanded::before
            content: ''
            position: absolute
            left: 27px
            top: 48px
            bottom: 0
            width: 2px
            background-color: rgba(0, 0, 0, 0.12)
            z-index: 1

    &__group-header
        position: relative
        display: flex
        align-items: center
        padding: 12px 16px
        cursor: pointer
        background-color: white
        transition: background-color 0.15s

        &:hover
            background-color: #fafafa

    &__expand-icon
        margin-right: 8px
        color: rgba(0, 0, 0, 0.6)
        font-size: 20px

        &--subgroup
            font-size: 18px

    &__tag-icon
        margin-right: 8px
        color: #1976d2
        font-size: 18px

    &__group-name
        font-weight: 500
        font-size: 14px
        color: rgba(0, 0, 0, 0.87)

    &__checkbox
        margin-right: 8px
        padding: 0
        
        ::v-deep .v-input--selection-controls
            margin-top: 0
            padding-top: 0

        ::v-deep .v-input__slot
            margin-bottom: 0

        ::v-deep .v-messages
            display: none

    &__subgroups
        background-color: white

    &__subgroup
        position: relative

        &--expanded::before
            content: ''
            position: absolute
            left: 59px
            top: 46px
            bottom: 0
            width: 2px
            background-color: rgba(0, 0, 0, 0.12)
            z-index: 1

    &__subgroup-header
        display: flex
        align-items: center
        padding: 10px 16px 10px 48px
        cursor: pointer
        background-color: white
        transition: background-color 0.15s

        &:hover
            background-color: #fafafa

    &__subgroup-name
        font-weight: 400
        font-size: 14px
        color: rgba(0, 0, 0, 0.75)

    &__group-items
        background-color: white

    &__item
        display: flex
        align-items: center
        padding: 8px 16px 8px 72px
        cursor: pointer
        transition: background-color 0.15s

        &:hover
            background-color: #f9f9f9

        &:last-child
            border-bottom: none

    &__item-name
        font-size: 14px
        color: rgba(0, 0, 0, 0.87)
</style>

